import { useRouter } from "next/router";

import { useState, useCallback, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useQuery } from "react-query";

import { useFormikContext } from "formik";

import useOrderCryptoPayment from "../useOrderCryptoPayment";
import useOrderFiatPayment from "../useOrderFiatPayment";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import { ShoppingCartItem } from "modules/Cart/ShoppingCart.interface";
import useCart from "modules/Cart/useCart";
import PaymentModal from "modules/Cart/PaymentModal";

import { selectChainId } from "redux/slices/metamask";

import CustomButton from "components/CustomButton";
import GridItem from "components/Grid/GridItem";
import GridContainer from "components/Grid/GridContainer";
import GradientBox from "components/GradientBox";
import InfoBox from "components/InfoBox";
import { INFO_BOX_TYPE } from "components/InfoBox/types";
import ConsentsFields from "components/ConsentsFields";

import { CHAIN_IDS } from "enums/chainIds";
import { PRICE_TYPES } from "enums/priceTypes";
import { PURPOSES_TYPES } from "enums/purposesTypes";
import { TRANSACTIONS_STATUSES } from "enums/transactionsStatuses";

import { RedeemPaymentDetail } from "interfaces/PaymentDetail.interface";

import { API_ROUTES, APP_ROUTES } from "config/routes";

import { captureException } from "utils/errors";
import { mapURLSearchParams } from "utils/query";

import { PaymentDetail } from "services/store/PaymentDetail.interface";

import fetcher from "lib/fetchJson";

type RedeemCostsProps = {
  purpose: PURPOSES_TYPES;
  showPriceType?: boolean;
  isRedeemable?: boolean;
  items: ShoppingCartItem[];
  onAfterRedeem?: () => void;
};

const defaultData = {
  paymentDetails: [],
  allowedChainIds: [],
  totalFiatPrice: null,
  hasAnyPartnerItem: false,
  hasOnlyPartnerItems: false,
};

const RedeemCosts = <
  T extends {
    priceType: string;
    shipping: { country: string };
    termsChecked: boolean;
    personalDataChecked: boolean;
    marketingChecked: boolean;
  },
>({
  purpose,
  items,
  showPriceType = false,
  isRedeemable = true,
  onAfterRedeem,
}: RedeemCostsProps): JSX.Element => {
  const { push } = useRouter();
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const { clearCart } = useCart();
  const formik = useFormikContext<T>();
  const chainId = useSelector(selectChainId);
  const handleCryptoPayment = useOrderCryptoPayment(purpose, items);
  const handleFiatPayment = useOrderFiatPayment(purpose, items);
  const { data, refetch } = useQuery({
    queryKey: [
      "redeemCosts",
      purpose,
      chainId,
      formik.values.priceType,
      formik.values?.shipping?.country,
      items,
    ],
    queryFn: async () => {
      try {
        const params =
          formik.values.priceType &&
          mapURLSearchParams({
            priceType: formik.values.priceType,
            chainId,
            ...(formik.values.shipping.country
              ? { countryCode: formik.values.shipping.country }
              : {}),
          });

        const response = await fetcher<{
          data: {
            paymentDetails: RedeemPaymentDetail[];
            totalFiatPrice: number;
            hasAnyPartnerItem: boolean;
            hasOnlyPartnerItems: boolean;
            allowedChainIds: CHAIN_IDS[];
          };
        }>(
          `${API_ROUTES.ORDER_PRICE}/${purpose}${
            params ? `?${params}` : `?chainId=${chainId}`
          }`,
          items,
          "POST",
        );

        return response.data;
      } catch (error) {
        captureException(error);
        return defaultData;
      }
    },
    enabled: !!chainId,
  });

  const {
    totalFiatPrice: fiatPrice,
    hasAnyPartnerItem,
    hasOnlyPartnerItems,
    allowedChainIds,
  } = data ?? defaultData;

  const paymentDetails = useMemo(() => {
    if (!data?.paymentDetails) {
      return [];
    }
    return data.paymentDetails.map(({ token, ...paymentDetail }) => ({
      paymentToken: token,
      ...paymentDetail,
    })) as unknown as PaymentDetail[];
  }, [data?.paymentDetails]);

  const handlePriceTypeChange = useCallback(
    (priceType: PRICE_TYPES) => {
      formik.setFieldValue("priceType", priceType);
    },
    [formik],
  );

  const handlePrePayment = useCallback(async () => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      return null;
    }

    return formik.values;
  }, [formik]);

  const toggleModal = () => {
    setModalOpen((state) => !state);
  };

  const handlePostRedeem = useCallback(
    async (transactionStatus: TRANSACTIONS_STATUSES): Promise<void> => {
      if (transactionStatus !== TRANSACTIONS_STATUSES.SUCCESS) return;

      clearCart();

      if (onAfterRedeem) {
        onAfterRedeem();
        toggleModal();
      } else {
        await push(APP_ROUTES.MY_ITEMS);
      }
    },
    [clearCart, onAfterRedeem, push],
  );

  const handleSubmit = async () => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      return;
    }
    toggleModal();
  };

  useEffect(() => {
    const onPriceChanged = () => {
      refetch();
    };
    document.addEventListener("price-changed", onPriceChanged);

    return () => {
      document.removeEventListener("price-changed", onPriceChanged);
    };
  }, [refetch]);

  return (
    <>
      <Box mt={2}>
        {showPriceType && (fiatPrice === null || fiatPrice > 0) && (
          <>
            <Typography component="h4" variant="h4" gutterBottom>
              Box type
            </Typography>
            <Typography variant="subtitle1" mb={2}>
              Choose the preferable type of packing for your shipment.
            </Typography>
          </>
        )}
        {showPriceType && (fiatPrice === null || fiatPrice > 0) && (
          <GridContainer my={1}>
            <GridItem xs={12} sm={6}>
              <GradientBox
                boxProps={{
                  sx: { width: { xs: "100%", md: "100%" } },
                  position: "relative",
                  mb: 2,
                }}
                height={100}
                isActive={formik.values.priceType === PRICE_TYPES.STANDARD}
                title="Standard Box"
                description="Basic folio pack sent in e-commerce bag"
                tooltipText={
                  <Typography variant="body2">
                    Item is shipped in standard type packaging: foil pack sent
                    in e-commerce bags containing a thank you note.
                  </Typography>
                }
                onClick={() => handlePriceTypeChange(PRICE_TYPES.STANDARD)}
              />
            </GridItem>
            <GridItem xs={12} sm={6}>
              <GradientBox
                boxProps={{
                  sx: { width: { xs: "100%", md: "100%" } },
                  position: "relative",
                }}
                height={100}
                isActive={formik.values.priceType === PRICE_TYPES.PREMIUM}
                title="Premium Box"
                description="Exclusive, branding packaging with additional gifts"
                tooltipText={
                  <>
                    <Typography variant="body2">
                      The Premium Box is an exclusive version of our shipment,
                      where all items are specially packed in the custom Uniqly
                      boxes or branded packing of our Partners. This box is
                      filled with decorative hay and contains framed
                      certificate, branded Uniqly's or Partner's stickers, sweet
                      milk candies, and thank you note.
                    </Typography>
                    <Typography variant="body2" mt={0.5}>
                      Premium Shipping is only available for items produced by
                      Uniqly.
                    </Typography>
                  </>
                }
                onClick={() => handlePriceTypeChange(PRICE_TYPES.PREMIUM)}
                isDisabled={hasOnlyPartnerItems}
              />
            </GridItem>
          </GridContainer>
        )}
        {showPriceType &&
          hasAnyPartnerItem &&
          formik.values.priceType === PRICE_TYPES.PREMIUM && (
            <InfoBox type={INFO_BOX_TYPE.INFO}>
              Only items produced by Uniqly will be sent in the Premium Box.
            </InfoBox>
          )}
        <Box display="flex" flexDirection="column">
          <ConsentsFields
            onChange={formik.setFieldValue}
            errors={formik.errors}
            isValid={formik.isValid}
          />
        </Box>
        {isRedeemable && fiatPrice !== null && fiatPrice === 0 && (
          <InfoBox type={INFO_BOX_TYPE.INFO} sx={{ mt: 2 }}>
            This transaction is currently free. It's a time-limited promotion,
            take it now!
          </InfoBox>
        )}
        <CustomButton
          onClick={handleSubmit}
          disabled={!isRedeemable}
          minWidth
          sx={{ mt: 2 }}
        >
          Order
        </CustomButton>
      </Box>
      {isModalOpen ? (
        <PaymentModal
          isOpen={isModalOpen}
          onClose={toggleModal}
          modalType="REDEEM"
          allowedChainIds={allowedChainIds}
          paymentDetails={paymentDetails}
          isFree={fiatPrice !== null && fiatPrice === 0}
          totalFiatPrice={fiatPrice ?? 0}
          onAfterPayment={handlePostRedeem}
          onBeforePayment={handlePrePayment}
          onCryptoPayment={handleCryptoPayment}
          onFiatPayment={handleFiatPayment}
        />
      ) : null}
    </>
  );
};

export default RedeemCosts;
